import { FC, useState } from 'react';
import toast from 'react-hot-toast';
import { WrapInLayout } from '../layouts';
import { getErrorFromCatch } from '../api/api';
import { IPageParams } from '../api/IApi';
import { orderApi } from '../api/order.api';
import { msg } from '../msg';
import { downloadBlob } from '../utils/downloadBlob';
import OrdersReportList from '../components/orders/reports/OrdersReportList';
import { UNSHIPPED_COLUMNS, unshippedRow } from '../components/orders/reports/reportColumns';
import { IDateRange, presetRange } from '../components/orders/orderDatePresets';
import { readPageSize } from '../components/common/table/tablePageSize';

const VIEW = 'unshipped-orders';

const UnshippedOrdersPage: FC = () => {
  const [range, setRange] = useState<IDateRange>(() => presetRange('month'));
  const [pageParams, setPageParams] = useState<IPageParams>(() => ({
    page: 0,
    size: readPageSize(VIEW, 30),
    q: '',
    sort: 'timeCreate',
    sortDir: 'desc',
  }));

  const { data: orders, isFetching, isError, error } = orderApi.useGetUnshippedOrdersQuery({
    page: pageParams.page,
    size: pageParams.size,
    sort: pageParams.sort,
    sortDir: pageParams.sortDir,
    createdFrom: range.createdFrom,
    createdTill: range.createdTill,
  });

  const [downloadCsv, { isFetching: isDownloading }] =
    orderApi.useLazyDownloadUnshippedOrdersCsvQuery();

  const handleDownload = async () => {
    const result = await downloadCsv({
      sort: pageParams.sort,
      sortDir: pageParams.sortDir,
      createdFrom: range.createdFrom,
      createdTill: range.createdTill,
    });

    if (result.data) {
      downloadBlob(result.data, `unshipped-orders-${new Date().toISOString().slice(0, 10)}.csv`);
      return;
    }
    toast.error(`${msg('load-orders-filed')} ${getErrorFromCatch(result.error)}`);
  };

  return (
    <WrapInLayout title="Unshipped Orders" layout="dashboard">
      <h1 className="text-xl font-semibold mb-3">Unshipped Orders</h1>
      <OrdersReportList
        columns={UNSHIPPED_COLUMNS}
        row={unshippedRow}
        orders={orders}
        isFetching={isFetching}
        isError={isError}
        error={error}
        range={range}
        onRangeChange={(next) => {
          setRange(next);
          // A new range is a new result set; staying on page 7 of the old one
          // lands on an empty table.
          setPageParams((prev) => ({ ...prev, page: 0 }));
        }}
        pageParams={pageParams}
        onPageParamsChange={(next) => setPageParams((prev) => ({ ...prev, ...next }))}
        storageKey={VIEW}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
    </WrapInLayout>
  );
};

export default UnshippedOrdersPage;
